import { TOOLCALL_NAME_DISPLAY_MAP } from '@/common/components/wavvest-planning/constants';
import type { Message } from '@/common/components/wavvest-planning/types';

type AgentMessage = {
  type?: unknown;
  role?: unknown;
  content?: unknown;
  name?: unknown;
  tool_calls?: unknown;
};

type ConversationSender = Message['sender'];

const DISPLAYABLE_SENDERS = new Set<ConversationSender>(['human', 'ai', 'system', 'tool']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return null;
}

function normalizeSender(message: AgentMessage): ConversationSender | null {
  const rawType = typeof message.type === 'string' ? message.type : message.role;
  if (rawType === 'user') return 'human';
  if (rawType === 'assistant') return 'ai';
  if (typeof rawType !== 'string') return null;
  return DISPLAYABLE_SENDERS.has(rawType as ConversationSender)
    ? (rawType as ConversationSender)
    : null;
}

export function extractMessageText(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (!Array.isArray(content)) {
    return '';
  }

  return content
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      if (isRecord(item) && typeof item.text === 'string') {
        return item.text;
      }
      if (isRecord(item) && typeof item.content === 'string') {
        return item.content;
      }
      return '';
    })
    .join('');
}

export function mapAgentMessagesToConversation(historyMessages: AgentMessage[]): Message[] {
  return historyMessages.flatMap((msg) => {
    const sender = normalizeSender(msg);
    if (!sender) {
      return [];
    }

    let content = extractMessageText(msg.content);

    if (sender === 'tool') {
      const toolName = typeof msg.name === 'string' ? msg.name : '';
      if (!toolName) {
        return [];
      }
      content =
        TOOLCALL_NAME_DISPLAY_MAP[toolName as keyof typeof TOOLCALL_NAME_DISPLAY_MAP] ?? toolName;
    }

    const hasToolCalls = Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0;
    if (!content.trim() || (sender === 'ai' && hasToolCalls)) {
      return [];
    }

    return [{ sender, content }];
  });
}

export function extractHistoryMessagesFromResponse(response: unknown): AgentMessage[] {
  if (Array.isArray(response)) {
    return response as AgentMessage[];
  }

  if (isRecord(response) && Array.isArray(response.messages)) {
    return response.messages as AgentMessage[];
  }

  return [];
}

export function mapHistoryResponseToConversation(response: unknown): Message[] {
  return mapAgentMessagesToConversation(extractHistoryMessagesFromResponse(response));
}

export function extractConversationMessagesFromUpdate(payload: unknown): Message[] {
  const messages: Message[] = [];

  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isRecord(value)) {
      return;
    }

    if (Array.isArray(value.messages)) {
      messages.push(...mapAgentMessagesToConversation(value.messages as AgentMessage[]));
    }

    Object.entries(value).forEach(([key, child]) => {
      if (key !== 'messages') {
        visit(child);
      }
    });
  };

  visit(payload);
  return messages;
}

/**
 * Extracts orchestrator graph node names from an `updates` / `custom` payload.
 *
 * Per the weave-x convention (see ai-v4 PR #51), a node's partial state is keyed
 * by node name. The names can appear either directly at the payload root
 * (`{ <node_name>: <partial_state> }`) or nested one level under `data`
 * (`{ data: { <node_name>: <partial_state> } }`), so we unwrap `data` when it is
 * present. Only record-valued keys are treated as nodes, which filters out
 * scalar metadata like `run_id`/`thread_id`. Unknown keys are harmless
 * downstream — `phaseForNodeNames` ignores anything not in `NODE_PHASE`.
 */
export function extractNodeNamesFromUpdate(payload: unknown): string[] {
  if (!isRecord(payload)) {
    return [];
  }

  const container = isRecord(payload.data) ? payload.data : payload;
  return Object.keys(container).filter((key) => isRecord(container[key]));
}

export function extractVisualizationHtmlFromState(state: unknown): string {
  if (!isRecord(state)) {
    return '';
  }

  // Release-gate updates persist generated reports as
  // `report_history[].final_report.viz_code`.
  if (typeof state.viz_code === 'string') {
    return state.viz_code;
  }

  const values = isRecord(state.values) ? state.values : state;

  const finalReport = values.final_report;
  if (isRecord(finalReport) && typeof finalReport.viz_code === 'string') {
    return finalReport.viz_code;
  }

  if (Array.isArray(values.report_history)) {
    for (let index = values.report_history.length - 1; index >= 0; index -= 1) {
      const historyEntry = values.report_history[index];
      if (!isRecord(historyEntry)) continue;

      const historicalReport = historyEntry.final_report;
      if (isRecord(historicalReport) && typeof historicalReport.viz_code === 'string') {
        return historicalReport.viz_code;
      }
    }
  }

  const finalOutput = values.final_output;
  if (isRecord(finalOutput) && typeof finalOutput.viz_html === 'string') {
    return finalOutput.viz_html;
  }

  const vizCurrent = values.viz_current;
  if (isRecord(vizCurrent) && typeof vizCurrent.full_html === 'string') {
    return vizCurrent.full_html;
  }

  // `report` is a sibling of `values`/`state` in the REST response shape
  // (ChatHistoryResponse/GetThreadStateResponse), not nested inside it — check
  // the pre-unwrap `state`, not `values`. For WS payloads (no `.values` key),
  // `values` already equals `state`, so this covers both shapes.
  const report = state.report;
  if (isRecord(report) && typeof report.html === 'string') {
    return report.html;
  }

  if (typeof values.latestVisualizationCode === 'string') {
    return values.latestVisualizationCode;
  }

  return '';
}

export function extractThreadNameFromState(state: unknown, fallback = 'Untitled Session'): string {
  if (!isRecord(state)) {
    return fallback;
  }

  const metadata = isRecord(state.metadata) ? state.metadata : undefined;
  const metadataTitle = firstNonEmptyString(metadata?.title, metadata?.sessionTitle);
  if (metadataTitle) {
    return metadataTitle;
  }

  const values = isRecord(state.values) ? state.values : state;
  const threadName = values.threadName;

  if (typeof threadName === 'string' && threadName.trim()) {
    return threadName;
  }

  if (Array.isArray(values.messages)) {
    const firstHumanMessage = values.messages.find((message) => {
      if (!isRecord(message)) {
        return false;
      }

      return normalizeSender(message) === 'human';
    });

    if (isRecord(firstHumanMessage)) {
      const title = extractMessageText(firstHumanMessage.content).trim().replace(/\s+/g, ' ');
      if (title) {
        return title.slice(0, 20);
      }
    }
  }

  return fallback;
}

export function hasThreadNameInState(state: unknown): boolean {
  if (!isRecord(state)) {
    return false;
  }

  const values = isRecord(state.values) ? state.values : state;
  return typeof values.threadName === 'string' && values.threadName.trim().length > 0;
}

export function hasPersistedThreadTitle(state: unknown): boolean {
  if (!isRecord(state)) {
    return false;
  }

  const metadata = isRecord(state.metadata) ? state.metadata : undefined;
  const metadataTitle = firstNonEmptyString(metadata?.title, metadata?.sessionTitle);
  if (metadataTitle) {
    return true;
  }

  return hasThreadNameInState(state);
}

export function extractVisualizationHtmlFromUpdate(payload: unknown): string {
  const candidates: string[] = [];

  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isRecord(value)) {
      return;
    }

    const html = extractVisualizationHtmlFromState(value);
    if (html) {
      candidates.push(html);
    }

    Object.values(value).forEach(visit);
  };

  visit(payload);
  return candidates.at(-1) ?? '';
}

export function isLegacyThreadMetadata(metadata: unknown): boolean {
  if (!isRecord(metadata)) {
    return false;
  }

  return metadata.is_legacy === true;
}
